import { useRef, useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import DiamondDivider from './components/DiamondDivider';
import ProductShowcase from './components/ProductShowcase';
import OrderForm, { OrderData } from './components/OrderForm';
import PaymentSection from './components/PaymentSection';
import ContactFooter from './components/ContactFooter';
import ConfirmationModal from './components/ConfirmationModal';
import PhotoGallery from './components/PhotoGallery';

const HomePage = () => {
  const orderRef = useRef<HTMLDivElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [showStickyBtn, setShowStickyBtn] = useState(false);
  const [formVisible, setFormVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBtn(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const el = orderRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFormVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToOrder = () => {
    const el = document.getElementById('order');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSuccess = (data: OrderData) => {
    setOrderData(data);
    setShowModal(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: '#1e1e1e',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4'%3E%3Crect width='4' height='4' fill='%231e1e1e'/%3E%3Crect x='0' y='0' width='1' height='1' fill='%23222'/%3E%3Crect x='2' y='2' width='1' height='1' fill='%23222'/%3E%3C/svg%3E")`,
      }}
    >
      <Navbar />

      {/* Landing / Hero */}
      <HeroSection onOrderClick={scrollToOrder} />

      <DiamondDivider />

      {/* Product Showcase */}
      <ProductShowcase onOrderClick={scrollToOrder} />

      <DiamondDivider />

      {/* Order Form */}
      <div ref={orderRef}>
        <OrderForm onSuccess={handleSuccess} />
      </div>

      <DiamondDivider />

      {/* Payment Instructions */}
      <PaymentSection />

      <DiamondDivider />

      {/* Contact + Footer */}
      <ContactFooter />

      {/* Confirmation Modal */}
      {showModal && (
        <ConfirmationModal
          onClose={() => setShowModal(false)}
          orderData={orderData}
        />
      )}

      {/* Sticky Dispatch Button */}
      <div
        className="fixed bottom-8 right-6 z-50 transition-all duration-500"
        style={{
          opacity: showStickyBtn && !formVisible ? 1 : 0,
          transform: showStickyBtn && !formVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
          pointerEvents: showStickyBtn && !formVisible ? 'auto' : 'none',
        }}
      >
        <button
          onClick={scrollToOrder}
          className="relative group flex items-center gap-3 px-6 py-4 cursor-pointer whitespace-nowrap"
          style={{
            background: '#CE1126',
            border: '2px solid #C0C0C0',
            fontFamily: '"Black Ops One", "Impact", sans-serif',
            boxShadow: '0 0 0 1px #CE1126, 4px 4px 0px #8a0b18',
          }}
        >
          {/* Pulse ring */}
          <span className="absolute -inset-1 rounded-none animate-ping opacity-20 bg-[#CE1126] pointer-events-none" />

          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-alarm-warning-fill text-[#FFD700] text-base" />
          </span>
          <span className="text-white text-xs tracking-[0.25em] uppercase font-black">
            DISPATCH ORDER
          </span>
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-right-line text-white text-sm" />
          </span>
        </button>
      </div>
    </div>
  );
};

export default HomePage;
